import { delay } from "@utils/asyncUtils";
import type { ApiResult } from "@utils/httpClientCore";
import { hasProp } from "@utils/assertion";

export type Result<T> = { state: "ERROR"; errorCode: string | null } | { state: "SUCCESS"; value: T } | { state: "IGNORE" };

export async function saveAsyncRequest<T, V extends unknown[]>(call: (...params: V) => Promise<ApiResult<T>>, isLastRequest: boolean | ((...params: V) => boolean), ...params: V): Promise<Result<T>> {
  const checkIsLastRequest = (isLastRequest: boolean | ((...params: V) => boolean), ...params: V): boolean => {
    if (typeof isLastRequest === "boolean") {
      return isLastRequest;
    }
    return isLastRequest(...params);
  };
  const response = await call(...params);
  if (response.kind === "error") {
    if (checkIsLastRequest(isLastRequest, ...params)) {
      return { state: "ERROR", errorCode: response.errorCode };
    }
    return { state: "IGNORE" };
  }
  if (checkIsLastRequest(isLastRequest, ...params)) {
    const value = hasProp(response, "data") ? response.data : (undefined as T);
    return { state: "SUCCESS", value };
  }
  return { state: "IGNORE" };
}

/**
 * To be used with endpoints, that have been generated with the old generator.
 * Otherwise use the new `saveAsyncRequest`.
 */
export async function saveAsyncRequestOld<T, V extends unknown[]>(call: (...params: V) => Promise<T>, isLastRequest: boolean | ((...params: V) => boolean), ...params: V): Promise<Result<T>> {
  const checkIsLastRequest = (isLastRequest: boolean | ((...params: V) => boolean), ...params: V): boolean => {
    if (typeof isLastRequest === "boolean") {
      return isLastRequest;
    }
    return isLastRequest(...params);
  };
  try {
    const response = await call(...params);
    if (checkIsLastRequest(isLastRequest, ...params)) {
      return { state: "SUCCESS", value: response };
    }
    return { state: "IGNORE" };
  } catch (e) {
    if (checkIsLastRequest(isLastRequest, ...params)) {
      if (e instanceof Error) {
        console.warn(e.message);
      }
      return { state: "ERROR", errorCode: null };
    }
    return { state: "IGNORE" };
  }
}

export type SynchronizedQueue = {
  run: (fn: () => Promise<void>) => void;
};
export const createSynchronizedQueue = (): SynchronizedQueue => {
  let currentAsyncCall: Promise<void> = Promise.resolve();

  const run = (fn: () => Promise<void>): void => {
    // eslint-disable-next-line ga-rules/no-unsafe-value-discard
    currentAsyncCall = currentAsyncCall.then(fn);
  };

  return {
    run,
  };
};

export const SafeAsyncController = <T, Params extends unknown[]>(
  fn: (...params: Params) => Promise<{ kind: "success"; data: T } | { kind: "error"; message: string }>,
): {
  run: (...params: Params) => Promise<{ kind: "success"; data: T } | { kind: "error"; message: string } | { kind: "ignore" }>;
} => {
  let count = 0;
  const run = async (...params: Params): Promise<{ kind: "success"; data: T } | { kind: "error"; message: string } | { kind: "ignore" }> => {
    count++;
    const myCount = count;

    await delay(100);
    if (count !== myCount) {
      return { kind: "ignore" };
    }

    const result = await fn(...params);
    if (count === myCount) {
      return result;
    } else {
      return { kind: "ignore" };
    }
  };

  return {
    run,
  };
};

export function createUnexpectedErrorMessage(message?: string, errorCode?: string | null): string {
  const messages = [message ?? "Unexpected error."];
  if (errorCode !== undefined && errorCode !== null) {
    messages.push(`Error code: ${errorCode}`);
  }
  return messages.join(" ");
}
